import { useEffect, useState } from "react";
import { useStaticTranslation } from "@/lib/staticTranslations";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import PageTransition from "@/components/PageTransition";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Trash2, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ContactSubmission {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  message: string;
  created_at: string | null;
}

const AdminContactMessages = () => {
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const { t: ct } = useStaticTranslation("confirmDialog");
  const [isAdmin, setIsAdmin] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    checkAdminAndFetch();
  }, []);

  const checkAdminAndFetch = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      console.log("Current user:", user?.id);
      
      if (!user) {
        toast.error("יש להתחבר כדי לגשת לעמוד זה");
        navigate("/auth");
        return;
      }

      // Check if user is admin
      const { data: roleData, error: roleError } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();

      console.log("Role check result:", { roleData, roleError });

      if (roleError) {
        console.error("Role check error:", roleError);
        toast.error("שגיאה בבדיקת הרשאות");
        navigate("/profile");
        return;
      }

      if (!roleData) {
        toast.error("אין לך הרשאה לגשת לעמוד זה");
        navigate("/profile");
        return;
      }

      console.log("User is admin, fetching submissions...");
      setIsAdmin(true);
      await fetchSubmissions();
    } catch (error) {
      console.error("Error checking admin status:", error);
      toast.error("שגיאה בבדיקת הרשאות");
      navigate("/profile");
    }
  };

  const fetchSubmissions = async () => {
    try {
      console.log("Fetching contact submissions...");
      
      const { data, error } = await supabase
        .from("contact_submissions")
        .select("*")
        .order("created_at", { ascending: false });

      console.log("Fetch result:", { data, error });

      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }
      
      setSubmissions(data || []);
      console.log("Submissions loaded successfully:", data?.length);
    } catch (error) {
      console.error("Error fetching submissions:", error);
      toast.error("שגיאה בטעינת ההודעות");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const { error } = await supabase
        .from("contact_submissions")
        .delete()
        .eq("id", deleteId);

      if (error) throw error;

      setSubmissions(submissions.filter((s) => s.id !== deleteId));
      toast.success("ההודעה נמחקה בהצלחה");
    } catch (error) {
      console.error("Error deleting submission:", error);
      toast.error("שגיאה במחיקת ההודעה");
    } finally {
      setDeleteId(null);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "לא זמין";
    return new Date(dateString).toLocaleDateString("he-IL", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <PageTransition>
      <div className="min-h-screen bg-background py-12 px-4">
        <div className="container mx-auto max-w-7xl">
          <h1 className="text-4xl font-bold text-foreground mb-8">
            ניהול הודעות צור קשר
          </h1>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">טוען הודעות...</p>
            </div>
          ) : submissions.length === 0 ? (
            <div className="text-center py-12 bg-muted/30 rounded-lg">
              <p className="text-muted-foreground">אין הודעות להצגה</p>
            </div>
          ) : (
            <div className="bg-card rounded-lg shadow-sm border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-right">שם</TableHead>
                    <TableHead className="text-right">אימייל</TableHead>
                    <TableHead className="text-right">טלפון</TableHead>
                    <TableHead className="text-right">הודעה</TableHead>
                    <TableHead className="text-right">תאריך</TableHead>
                    <TableHead className="text-right">פעולות</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {submissions.map((submission) => (
                    <TableRow key={submission.id}>
                      <TableCell className="font-medium">
                        {submission.name}
                      </TableCell>
                      <TableCell>
                        <a
                          href={`mailto:${submission.email}`}
                          className="flex items-center gap-2 text-primary hover:underline"
                        >
                          <Mail className="h-4 w-4" />
                          {submission.email}
                        </a>
                      </TableCell>
                      <TableCell>
                        {submission.phone ? (
                          <a
                            href={`tel:${submission.phone}`}
                            className="flex items-center gap-2 text-primary hover:underline"
                          >
                            <Phone className="h-4 w-4" />
                            {submission.phone}
                          </a>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-md">
                        <p className="line-clamp-2 text-sm">
                          {submission.message}
                        </p>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(submission.created_at)}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteId(submission.id)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{ct("deleteMessage", "האם למחוק הודעה זו?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {ct("deleteMessageDesc", "פעולה זו לא ניתנת לביטול. ההודעה תימחק לצמיתות מהמערכת.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{ct("cancel", "ביטול")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>{ct("delete", "מחק")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageTransition>
  );
};

export default AdminContactMessages;
